import React, { useEffect, useState, useRef, useCallback } from "react";
import { AiOutlineMenuUnfold } from "react-icons/ai";
import {
  FaBell,
  FaWallet,
  FaCheck,
  FaTrash,
  FaCheckDouble,
  FaInfoCircle,
  FaExclamationTriangle,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";
import AdminProfileDropDown from "../../Components/Admin/AdminProfileDropDown";
import axios from "axios";
import { RefreshCw, X, Bell, BellOff, Download } from "lucide-react";
import { usePWA } from "../../Context/PWAContext";

import PuccImg from "../../Assets/Logos/pucc-logo.png";
import AadhaarImg from "../../Assets/Logos/Aadhaar.png";
import PanImg from "../../Assets/Logos/pan.jpg";
import VoterImg from "../../Assets/Logos/vote.png";
import RcImg from "../../Assets/Logos/RCLOGO.jpeg";
import DlImg from "../../Assets/Logos/DL.jpg";
import ChallanImg from "../../Assets/Logos/challan.jfif";
import IDFC from "../../Assets/Logos/idfc.jpg";
import SBI from "../../Assets/Logos/sbi.png";
import Livquick from "../../Assets/Logos/quickwallet.png";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const getNotifMeta = (type) => {
  switch (type) {
    case "sub-agent-approval":
      return {
        icon: FaUserCheck,
        color: "text-violet-500",
        bg: "bg-violet-50",
        accent: "#7c3aed",
      };
    case "warning":
      return {
        icon: FaExclamationTriangle,
        color: "text-amber-500",
        bg: "bg-amber-50",
        accent: "#d97706",
      };
    case "success":
      return {
        icon: FaCheck,
        color: "text-emerald-500",
        bg: "bg-emerald-50",
        accent: "#059669",
      };
    default:
      return {
        icon: FaInfoCircle,
        color: "text-blue-500",
        bg: "bg-blue-50",
        accent: "#2563eb",
      };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// NotificationPanel
// ─────────────────────────────────────────────────────────────────────────────
const NotificationPanel = ({ isOpen, onClose, userId, onUnreadChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("unread");
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/notifications/user/${userId}`,
      );
      const data = res.data || [];
      setNotifications(data);
      onUnreadChange(data.filter((n) => !n.isRead).length);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [userId, onUnreadChange]);

  // Fetch whenever the panel opens
  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    if (isOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onClose]);

  const markAsRead = async (id) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/read/${id}`,
      );
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
      onUnreadChange(
        notifications.filter((n) => !n.isRead && n._id !== id).length,
      );
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/read-all/${userId}`,
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      onUnreadChange(0);
    } catch {}
  };

  const deleteNotif = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/notifications/${id}`,
      );
      const updated = notifications.filter((n) => n._id !== id);
      setNotifications(updated);
      onUnreadChange(updated.filter((n) => !n.isRead).length);
    } catch {}
  };

  // ── Approve / Reject handler ──────────────────────────────────────────────
  const handleApproveReject = async (notif, action, e) => {
    e.stopPropagation();

    // Immediately hide both buttons by optimistically updating status
    // This prevents any flicker or double-click
    setNotifications((prev) =>
      prev.map((n) =>
        n._id === notif._id
          ? { ...n, status: action, isRead: true, _actioning: true }
          : n,
      ),
    );
    onUnreadChange(
      notifications.filter((n) => !n.isRead && n._id !== notif._id).length,
    );

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/approve/${notif._id}`,
        { action },
      );
      // Confirm final state — remove the _actioning flag
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notif._id
            ? { ...n, status: action, isRead: true, _actioning: false }
            : n,
        ),
      );
    } catch (err) {
      console.error("Approve/Reject failed:", err?.response?.data || err);
      // Rollback on failure — restore pending status so buttons reappear
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notif._id
            ? { ...n, status: "pending", isRead: false, _actioning: false }
            : n,
        ),
      );
      onUnreadChange(notifications.filter((n) => !n.isRead).length + 1);
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const displayed =
    filter === "unread"
      ? notifications.filter((n) => !n.isRead)
      : notifications;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[200] transition-all duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{
          background: "rgba(15,23,42,0.15)",
          backdropFilter: isOpen ? "blur(2px)" : "none",
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`
          fixed right-4 z-[201] w-[390px] max-w-[calc(100vw-1.5rem)]
          rounded-2xl overflow-hidden bg-white
          border border-slate-200/70
          shadow-[0_32px_72px_rgba(0,0,0,0.16),0_4px_20px_rgba(0,0,0,0.08)]
          transition-all duration-[320ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]
          ${
            isOpen
              ? "opacity-100 translate-y-0 scale-100"
              : "opacity-0 -translate-y-3 scale-[0.97] pointer-events-none"
          }
        `}
        style={{ top: "88px" }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
              <Bell size={17} className="text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-[15px] leading-none">
                Notifications
              </p>
              <p className="text-blue-300/80 text-[11px] mt-[3px]">
                {unreadCount > 0
                  ? `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}`
                  : "You're all caught up ✓"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-300 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-all duration-150"
              >
                <FaCheckDouble size={10} />
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-300 hover:text-white transition-all duration-150"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* ── Filter tabs ── */}
        <div className="flex items-center gap-1 px-4 py-2.5 bg-slate-50/80 border-b border-slate-100">
          {["all", "unread"].map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`
                flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[12px] font-semibold capitalize transition-all duration-150
                ${
                  filter === tab
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-200"
                }
              `}
            >
              {tab}
              {tab === "unread" && unreadCount > 0 && (
                <span
                  className={`px-1.5 py-px rounded-full text-[10px] font-black ${
                    filter === "unread"
                      ? "bg-white/20 text-white"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={fetchNotifications}
            className="ml-auto w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 flex items-center justify-center transition-all duration-150"
            title="Refresh"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {/* ── List ── */}
        <div className="overflow-y-auto" style={{ maxHeight: "430px" }}>
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 shrink-0" />
                  <div className="flex-1 space-y-2 pt-1">
                    <div className="h-3 bg-slate-100 rounded-lg w-3/4" />
                    <div className="h-2.5 bg-slate-100 rounded-lg w-full" />
                    <div className="h-2 bg-slate-100 rounded-lg w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <BellOff size={26} className="text-slate-300" />
              </div>
              <p className="text-slate-700 font-bold text-[14px]">
                {filter === "unread"
                  ? "No unread notifications"
                  : "Nothing here yet"}
              </p>
              <p className="text-slate-400 text-[12px] mt-1 leading-relaxed">
                {filter === "unread"
                  ? "You've read everything. Great job!"
                  : "We'll notify you when something important arrives."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {displayed.map((notif) => {
                const { icon: Icon, color, bg } = getNotifMeta(notif.type);
                const isSubAgentPending =
                  notif.type === "sub-agent-approval" &&
                  notif.status === "pending" &&
                  !notif._actioning;
                const isActioning = notif._actioning === true;

                return (
                  <div
                    key={notif._id}
                    onClick={() => !notif.isRead && markAsRead(notif._id)}
                    className={`
                      group relative flex gap-3 px-4 py-4 cursor-pointer
                      transition-colors duration-150
                      ${
                        !notif.isRead
                          ? "bg-blue-50/50 hover:bg-blue-50"
                          : "bg-white hover:bg-slate-50"
                      }
                    `}
                  >
                    {/* Unread left accent */}
                    {!notif.isRead && (
                      <div className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full bg-blue-500" />
                    )}

                    {/* Type icon */}
                    <div
                      className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0 mt-0.5`}
                    >
                      <Icon size={15} className={color} />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={`text-[13px] leading-snug ${
                            !notif.isRead
                              ? "font-bold text-slate-900"
                              : "font-semibold text-slate-600"
                          }`}
                        >
                          {notif.title}
                        </p>
                        <span className="text-[10px] text-slate-400 whitespace-nowrap shrink-0 mt-0.5">
                          {timeAgo(notif.createdAt)}
                        </span>
                      </div>
                      <p className="text-[12px] text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">
                        {notif.message}
                      </p>

                      {/* ── Status pill (already actioned) ── */}
                      {notif.status && notif.status !== "pending" && (
                        <span
                          className={`
                            inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full text-[10px] font-bold
                            ${
                              notif.status === "approved"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-600"
                            }
                          `}
                        >
                          {notif.status === "approved" ? (
                            <FaUserCheck size={9} />
                          ) : (
                            <FaUserTimes size={9} />
                          )}
                          {notif.status.charAt(0).toUpperCase() +
                            notif.status.slice(1)}
                        </span>
                      )}

                      {/* ── Processing spinner (while API call in flight) ── */}
                      {isActioning && (
                        <div className="flex items-center gap-1.5 mt-3">
                          <RefreshCw
                            size={11}
                            className="animate-spin text-slate-400"
                          />
                          <span className="text-[11px] text-slate-400 font-semibold">
                            Processing…
                          </span>
                        </div>
                      )}

                      {/* ── Approve / Reject buttons — only shown while status is "pending" ── */}
                      {isSubAgentPending && (
                        <div
                          className="flex items-center gap-2 mt-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) =>
                              handleApproveReject(notif, "approved", e)
                            }
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-400 hover:shadow-sm active:scale-95 transition-all duration-150 select-none"
                          >
                            <FaUserCheck size={9} />
                            Approve
                          </button>
                          <button
                            onClick={(e) =>
                              handleApproveReject(notif, "rejected", e)
                            }
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-400 hover:shadow-sm active:scale-95 transition-all duration-150 select-none"
                          >
                            <FaUserTimes size={9} />
                            Reject
                          </button>
                        </div>
                      )}

                      {/* Unread dot */}
                      {!notif.isRead && (
                        <div className="flex items-center gap-1.5 mt-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                          <span className="text-[10px] text-blue-500 font-semibold">
                            Tap to mark as read
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Delete */}
                    <button
                      onClick={(e) => deleteNotif(notif._id, e)}
                      className="
                        opacity-0 group-hover:opacity-100
                        w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100
                        flex items-center justify-center shrink-0
                        text-red-400 hover:text-red-600
                        transition-all duration-150 mt-0.5
                      "
                      title="Delete"
                    >
                      <FaTrash size={10} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {notifications.length > 0 && (
          <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <p className="text-[11px] text-slate-400">
              {displayed.length} of {notifications.length} shown
            </p>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
              >
                Clear all unread
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Navbar
// ─────────────────────────────────────────────────────────────────────────────
const Navbar = ({ toggleSidebar }) => {
  const { isInstalled, installApp } = usePWA();
  const [walletData, setWalletData] = useState({
    balance: 0,
    currency: "₹",
    isLoading: true,
    lastUpdated: null,
    transactions: [],
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellShake, setBellShake] = useState(false);
  const prevUnread = useRef(0);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = userData?._id;

  const breakingNews = [
    {
      id: 1,
      image: PuccImg,
      title: "GET YOUR PUCC CERTIFICATE NOW !!",
      whatsappNumber: "+919124578108",
      whatsappMessage:
        "Hey! I need to get my PUCC Certificate. Can you help me out? 🚗",
    },
    { id: 2, image: AadhaarImg, title: "GET YOUR AADHAR DONE HERE !!" },
    { id: 3, image: PanImg, title: "FIND YOUR PAN FROM AADHAR & PHONE NUMBER" },
    { id: 4, image: VoterImg, title: "DOWNLOAD YOUR VOTER ID CARD NOW !!" },
    { id: 5, image: RcImg, title: "FIND YOUR ALL RC DETAILS !" },
    { id: 6, image: DlImg, title: "KEEP YOUR DL IN YOUR POCKET !!" },
    { id: 7, image: ChallanImg, title: "CHECK YOUR CHALLAN DETAILS HERE !" },
    {
      id: 8,
      image: IDFC,
      title: "IDFC FASTag Service Available!",
      whatsappNumber: "+919124578108",
      whatsappMessage: "Hi, I want to apply for IDFC FASTag. Please assist me.",
    },
    {
      id: 9,
      image: SBI,
      title: "SBI FASTag Service Available!",
      whatsappNumber: "+919124578108",
      whatsappMessage: "Hi, I want to apply for SBI FASTag. Please assist me.",
    },
    {
      id: 10,
      image: Livquick,
      title: "LivQuik Fastag Service!",
      whatsappNumber: "+919124578108",
      whatsappMessage:
        "Hi, I want to get a FASTag through LivQuik Wallet. Please help.",
    },
  ];

  // Poll unread count every 30s
  useEffect(() => {
    const poll = async () => {
      if (!userId) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/notifications/user/${userId}`,
        );
        const count = (res.data || []).filter((n) => !n.isRead).length;
        if (count > prevUnread.current) {
          setBellShake(true);
          setTimeout(() => setBellShake(false), 700);
        }
        prevUnread.current = count;
        setUnreadCount(count);
      } catch {}
    };
    poll();
    const id = setInterval(() => {
      if (!notifOpen) poll();
    }, 10000);
    return () => clearInterval(id);
  }, [userId, notifOpen]);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % breakingNews.length);
    }, 3000);
    return () => clearInterval(id);
  }, [breakingNews.length]);

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      setIsRefreshing(true);
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.entityId) return;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wallet/${userData.entityId}`,
      );
      if (response.data.success) {
        setWalletData({
          balance: response.data.balance,
          currency: response.data.currency === "INR" ? "₹" : "$",
          isLoading: false,
          lastUpdated: new Date(response.data.lastUpdated),
          transactions: response.data.transactions || [],
        });
      }
    } catch {
      setWalletData((prev) => ({ ...prev, isLoading: false }));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleNewsClick = (news) => {
    if (news.whatsappNumber && news.whatsappMessage) {
      const encoded = encodeURIComponent(news.whatsappMessage);
      window.open(
        `https://wa.me/${news.whatsappNumber.replace(/[^\d+]/g, "")}?text=${encoded}`,
        "_blank",
      );
    }
  };

  const currentNews = breakingNews[currentIndex];

  return (
    <>
      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.8); }
        }
        @keyframes shimmer {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 1; }
        }
        @keyframes badgePop {
          0%   { transform: scale(0) rotate(-12deg); }
          60%  { transform: scale(1.3) rotate(4deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes bellShake {
          0%,100% { transform: rotate(0); }
          15%     { transform: rotate(-20deg); }
          35%     { transform: rotate(20deg); }
          55%     { transform: rotate(-12deg); }
          75%     { transform: rotate(12deg); }
          90%     { transform: rotate(-4deg); }
        }
        @keyframes ringPulse {
          0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
          70%  { box-shadow: 0 0 0 7px rgba(239,68,68,0); }
          100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
        }
        .anim-slide-in-up { animation: slideInUp 0.35s ease; }
        .anim-live-pulse  { animation: livePulse 1.2s ease-in-out infinite; }
        .anim-shimmer     { animation: shimmer 1.2s infinite; }
        .anim-badge-pop   { animation: badgePop 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .anim-bell-shake  { animation: bellShake 0.65s ease; }
        .anim-ring-pulse  { animation: ringPulse 1.5s ease-out infinite; }
      `}</style>

      {/* ══ NAVBAR ══ */}
      <nav
        className="
        flex items-center justify-between
        px-[10px] h-20 bg-white
        shadow-[0_1px_6px_rgba(0,0,0,0.09)]
        sticky top-0 z-[100] gap-4
      "
      >
        {/* Toggle */}
        <button
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
          className="bg-transparent border-none cursor-pointer flex items-center p-[6px] rounded-lg text-gray-700 shrink-0 hover:bg-gray-100 transition-colors duration-150"
        >
          <AiOutlineMenuUnfold size={24} />
        </button>

        {/* Desktop ticker */}
        <div className="flex-1 mx-[35px] hidden xl:grid grid-cols-[auto_auto_1fr] items-center gap-5 bg-slate-50 border border-slate-200 rounded-[10px] px-[14px] h-[38px] overflow-hidden min-w-0">
          <span className="flex items-center gap-[5px] bg-red-500 text-white text-[10.5px] font-extrabold tracking-widest px-[9px] py-[3px] rounded-[5px] shrink-0 whitespace-nowrap select-none">
            <span className="w-[6px] h-[6px] rounded-full bg-white inline-block shrink-0 anim-live-pulse" />
            LIVE
          </span>
          <div className="w-px h-5 bg-slate-300 shrink-0" />
          <div className="overflow-hidden flex items-center justify-center min-w-0">
            <div
              key={`desktop-${currentIndex}`}
              onClick={() => handleNewsClick(currentNews)}
              className={`flex items-center justify-center gap-[10px] anim-slide-in-up whitespace-nowrap overflow-hidden w-full ${currentNews.whatsappNumber ? "cursor-pointer" : "cursor-default"}`}
            >
              <img
                src={currentNews.image}
                alt=""
                className="w-[35px] h-[35px] object-contain shrink-0 rounded-[3px]"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <span className="text-[18px] font-bold text-slate-800 overflow-hidden text-ellipsis whitespace-nowrap">
                {currentNews.title}
              </span>
              {currentNews.whatsappNumber && (
                <span className="text-[11px] font-bold text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-[2px] shrink-0 whitespace-nowrap">
                  Click here →
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {/* Wallet */}
          <div className="flex items-center gap-[6px] bg-green-50 border border-green-200 rounded-lg px-[10px] py-[5px] cursor-pointer whitespace-nowrap hover:bg-green-100 transition-colors duration-150">
            <FaWallet className="text-green-600 text-[14px] shrink-0" />
            <span className="text-[13px] font-bold text-green-700 whitespace-nowrap">
              {walletData.isLoading ? (
                <span className="inline-block w-[60px] h-[13px] bg-green-200 rounded anim-shimmer" />
              ) : (
                `${walletData.currency} ${walletData.balance.toLocaleString("en-IN")}`
              )}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                fetchWalletBalance();
              }}
              disabled={isRefreshing}
              aria-label="Refresh balance"
              className="bg-transparent border-none cursor-pointer p-0 flex items-center text-green-600 shrink-0 disabled:cursor-not-allowed disabled:opacity-55"
            >
              <RefreshCw
                size={13}
                className={isRefreshing ? "animate-spin" : ""}
              />
            </button>
          </div>

          {/* ── Bell Button ── */}
          <div className="relative shrink-0">
            <button
              aria-label="Notifications"
              onClick={() => setNotifOpen((p) => !p)}
              className={`
                relative w-9 h-9 rounded-xl flex items-center justify-center
                transition-all duration-200
                ${
                  notifOpen
                    ? "bg-slate-900 text-white shadow-lg scale-95"
                    : "bg-transparent text-gray-500 hover:bg-slate-100 hover:text-slate-800"
                }
                ${bellShake ? "anim-bell-shake" : ""}
              `}
            >
              <FaBell size={17} />

              {/* Badge */}
              {unreadCount > 0 && (
                <span
                  className="anim-badge-pop anim-ring-pulse absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-[4px] rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center leading-none border-2 border-white"
                  style={{ boxShadow: "0 2px 8px rgba(239,68,68,0.45)" }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Profile */}
          <AdminProfileDropDown />
        </div>
      </nav>

      {/* ══ MOBILE TICKER ══ */}
      <div className="xl:hidden flex items-center bg-slate-900 w-full h-10 overflow-hidden sticky top-[60px] z-[99] shadow-[0_2px_6px_rgba(0,0,0,0.25)]">
        <div className="flex items-center gap-[5px] bg-red-500 text-white text-[10px] font-extrabold tracking-widest uppercase px-3 h-full shrink-0 whitespace-nowrap select-none">
          <span className="w-[6px] h-[6px] rounded-full bg-white inline-block anim-live-pulse" />
          LIVE
        </div>
        <div className="w-px h-full bg-white/15 shrink-0" />
        <div className="flex-1 overflow-hidden flex items-center justify-center px-[10px] min-w-0">
          <div
            key={`mobile-${currentIndex}`}
            onClick={() => handleNewsClick(currentNews)}
            className={`flex items-center justify-center gap-2 anim-slide-in-up whitespace-nowrap overflow-hidden w-full ${currentNews.whatsappNumber ? "cursor-pointer" : "cursor-default"}`}
          >
            <img
              src={currentNews.image}
              alt=""
              className="w-[18px] h-[18px] object-contain shrink-0 rounded-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="text-[11.5px] font-semibold text-slate-100 overflow-hidden text-ellipsis whitespace-nowrap">
              {currentNews.title}
            </span>
            {currentNews.whatsappNumber && (
              <span className="text-[10px] font-bold text-green-400 shrink-0 whitespace-nowrap">
                Tap →
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ══ NOTIFICATION PANEL ══ */}
      <NotificationPanel
        isOpen={notifOpen}
        onClose={() => setNotifOpen(false)}
        userId={userId}
        onUnreadChange={(count) => {
          prevUnread.current = count;
          setUnreadCount(count);
        }}
      />
    </>
  );
};

export default Navbar;
